<template>
  <div ref="root" class="relative">
    <div class="pb-12 bg-primary flex gap-x-7.5">
      <div class="w-full">
        <div
          class="container my-4 flex items-center max-lg:justify-between gap-9"
        >
          <UiButton
            variant="clear"
            class="p-0 text-xs text-white font-normal lg:hidden"
            content-class="flex items-center gap-x-1"
            @click="filterDrawer = true"
          >
            <span>{{ $t("filterAndSort") }}</span>
            <UiIcon name="chevron_down" class="pointer-events-none size-3" />
          </UiButton>
          <div class="flex flex-wrap gap-2 max-w-[900px] max-lg:hidden">
            <UiButton
              v-for="(item, index) in staticFilters"
              :key="index"
              class="font-normal px-4"
              :class="{
                'border-black-40 text-black-40': !checkActiveStaticFilter(item),
              }"
              color="white"
              variant="outlined"
              size="sm"
              content-class="flex items-center gap-x-1"
              @click="applyStaticFilter(item)"
            >
              <span>{{ item.label }}</span>
            </UiButton>
            <UiButton
              class="font-normal border-black-40 text-black-40 px-4"
              color="white"
              variant="outlined"
              size="sm"
              content-class="flex items-center gap-x-1"
              @click="filterDrawer = true"
            >
              <span>{{ $t("allFilters") }}</span>
              <UiIcon name="plus" class="size-3 shrink-0 text-white" />
            </UiButton>
          </div>
          <div
            class="order-2 ml-auto flex items-center flex-shrink-0 md:gap-9 max-lg:hidden text-white"
          >
            <UiGridDesktopToggle v-model="gridSize.desktop" />
            <UiFilterUiSort
              btn-classes="font-normal text-text-secondary font-semibold truncate text-sm max-lg2:px-2.5 max-lg2:h-9.5 max-lg2:border max-lg2:border-gray-border max-lg2:w-full"
              @onChangeSort="onChangeSort"
            />
          </div>
        </div>
        <UiFilterDrawer
          v-model="filterDrawer"
          :applied-filters-count="appliedFiltersCount"
          :products-count="catalogData?.meta?.total || 0"
        >
          <div class="pb-4 sticky top-10 z-1 border-b border-white bg-primary">
            <UiFilterTags
              v-if="filterTags && !isEmpty(filterTags.tags)"
              :filter-tags="filterTags"
              chips
              class="px-4 pt-2"
            />
          </div>
          <UiFilter
            v-if="!pendingMinMax"
            :collection-ids="collectionIds"
            class="w-full"
            :hide-filter-type="
              plpType === plpTypes.engagementRing && $route.query.dsku
                ? ['DIAMOND_SHAPE']
                : undefined
            "
            :category-ids="categoryIds"
            :show-all="data.enable_all_filter"
            :show_categories_filters="data?.show_categories_filters"
            :show_collections_filters="data?.show_collections_filters"
            :min-max-data="minMaxDataRaw?.data"
            block-classes="border-b border-white p-4"
            @onChangeSort="onChangeSort"
          />
          <div class="space-y-4 p-4 text-center">
            <UiButton
              class="w-full"
              size="md"
              color="white"
              variant="outlined"
              @click="filterDrawer = false"
            >
              {{ $t("close") }}
            </UiButton>
            <UiButton
              variant="text"
              class="min-h-0 p-0 text-sm text-white capitalize"
              @click="filterTags.removeTagAll"
            >
              {{ $t("clearAllFilters") }}
            </UiButton>
          </div>
        </UiFilterDrawer>
        <div
          v-if="pending && (!infiniteScroll || !dataCatalog?.length)"
          class="mb-40 flex w-full items-start justify-center bg-primary py-28"
        >
          <img
            src="/images/loader.gif"
            alt="Loader"
            class="h-50 w-50 object-contain grayscale"
          />
        </div>
        <template v-else>
          <div ref="targetElTop" class="h-px"></div>
          <img
            v-if="prevDataLoading && infiniteScroll"
            src="/images/loader.gif"
            alt="Loader"
            class="absolute left-1/2 top-0 size-8 -translate-x-1/2 object-contain grayscale"
          />
          <div
            v-if="isEmpty(dataCatalog)"
            class="min min-h-[424px] text-center text-white pt-20"
          >
            {{ $t("sorryProductsNotFound") }}
          </div>
          <BlocksBlockProductsWithBanners
            v-else
            :data-catalog="dataCatalog || []"
            :page-data="pageData"
            :plp-type="plpType"
            :grid-size-desktop="gridSize.desktop"
            :grid-size-mobile="gridSize.mobile"
          />
          <p
            v-if="dataCatalog?.length && catalogData?.meta?.total"
            class="ui-text-xs text-white text-center pt-4"
          >
            {{ dataCatalog.length }} / {{ catalogData.meta.total }}
            {{ $t("productsViewed") }}
          </p>
          <div
            v-if="!isEmpty(dataCatalog) && catalogData?.meta?.last_page > 1"
            class="relative flex justify-center mt-8 min-h-8"
          >
            <UiPagination
              v-model="page"
              :total-pages="catalogData?.meta?.last_page || 0"
              :class="pending && infiniteScroll ? 'opacity-0' : ''"
              white
              class="hidden"
              @update:model-value="changePage"
            />
            <img
              v-if="pending && infiniteScroll"
              src="/images/loader.gif"
              alt="Loader"
              class="absolute left-1/2 top-0 size-8 -translate-x-1/2 object-contain grayscale"
            />
          </div>
          <div v-if="!infiniteScroll" ref="targetEl" class="h-px"></div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMounted, useStorage } from "@vueuse/core";
import { hash } from "ohash";
import {
  BlockPlpCatalog,
  CatalogData,
  PageData,
  StaticFilterItem,
} from "ecom-types";

import { GetCatalogParams } from "~/api";
import { plpTypes, filterQueryKey } from "@/configs";
import UiGridMobileToggle from "~/components/Ui/grid/UiGridMobileToggle.vue";

interface FullStaticFilterItem extends StaticFilterItem {
  filterGroup?: string;
  filterValue?: string;
}

const props = defineProps<{
  data: BlockPlpCatalog;
  plpType?: string;
  pageData: PageData | null;
}>();

const { t } = useI18n();

const { $api } = useNuxtApp();
const { setPLPPageLdSchema } = useJsonLdSchema();

const { allFilters } = useGeneralStore();

const gridSize = useStorage("plp-grid", { mobile: 2, desktop: 4 }, undefined, {
  initOnMounted: true,
});

const route = useRoute();
const router = useRouter();
const targetEl = ref(null);
const filterDrawer = ref(false);
const prevDataLoading = ref(false); // prevPending work incorrect with immediate FALSE
const isMounted = useMounted();

if (route.query.dShape) {
  const materials = route.query.materials
    ? Array.isArray(route.query.materials)
      ? route.query.materials
      : [route.query.materials]
    : [];

  if (route.query.dShape && !materials.includes(route.query.dShape as string)) {
    materials.push(route.query.dShape as string);
  }

  await router.replace({ query: { ...route.query, materials } });
}

const minMaxKey = computed(() => hash(["catalogRange", props.pageData?.slug]));
const { data: minMaxDataRaw, pending: pendingMinMax } =
  await useAsyncCachedData(minMaxKey, () => {
    return $api.products.getCatalogRanges(
      objectToQuery({
        categories_sku: props.data.categories.map((m) => m.sku as string),
        collections: props.data.collections.map((m) => m.id),
        all: 1,
      }),
    );
  });

const { dynamicParamsEvent, viewItemListEvent } = useEvents();

const root = ref<HTMLElement | null>(null);
const collectionIds = computed(() => {
  return props.data?.collections?.map((m) => m.id);
});

const categoryIds = computed(() => {
  return props.data?.categories?.map((m) => m.id);
});

const { page, filterParams, sortBy, filterTags, prevPage } = useCatalogFilters({
  minMaxData: minMaxDataRaw.value?.data,
  collectionIds: collectionIds.value,
  categoryIds: categoryIds.value,
  showAll: props.data.enable_all_filter,
  showCategoriesFilters: props.data?.show_categories_filters,
  showCollectionsFilters: props.data?.show_collections_filters,
});

const filter = computed(() => {
  const tempFilter: GetCatalogParams = {
    ...filterParams.value,
    categories_sku: isEmpty(filterParams.value.categories_sku)
      ? props.data.categories.map((m) => m.sku as string)
      : filterParams.value.categories_sku,
    collections: isEmpty(filterParams.value.collections)
      ? props.data.collections.map((m) => m.id)
      : filterParams.value.collections,
    materials_sku: isEmpty(filterParams.value.materials_sku)
      ? props.data.materials
          .map((m) => m.sku)
          .filter((sku) => {
            return allFilters(
              collectionIds.value,
              categoryIds.value,
            )?.materials?.find((i) => i.sku === sku);
          })
      : filterParams.value.materials_sku,
  };

  return objectToQuery(tempFilter);
});

const appliedFiltersCount = computed(() => {
  const count = filterTags.value.tags.length;
  return count || 0;
});

const staticFilters = computed<FullStaticFilterItem[]>(() => {
  return (props.data?.static_filters || []).reduce(
    (acc, el: FullStaticFilterItem) => {
      el = setStaticFilterValues(el);
      if (el.filterValue) {
        acc.push(el);
      }
      return acc;
    },
    [] as FullStaticFilterItem[],
  );
});

const debouncedFilter = refDebounced(filter, 300);
const prevFilter = computed(() => ({ ...filter.value, page: prevPage.value }));
const key = computed(() =>
  hash([`catalog-${props.pageData?.slug}`, unref(debouncedFilter.value)]),
);
const { data: catalogData, pending } = await useAsyncCachedData(
  key,
  () => {
    return $api.products.getCatalog(debouncedFilter);
  },
  {
    watch: [debouncedFilter],
  },
);

const prevKey = computed(() =>
  hash([`catalog-${props.pageData?.slug}`, unref(prevFilter.value)]),
);
const { data: prevCatalogData, execute } = await useAsyncCachedData(
  prevKey,
  () => {
    return $api.products.getCatalog(prevFilter);
  },
  {
    immediate: false,
  },
);

const dataCatalog = ref(catalogData.value?.data);
const infiniteScroll = ref(false);

watchEffect(() => {
  if (
    catalogData.value?.meta?.last_page &&
    page.value > catalogData.value.meta.last_page
  ) {
    page.value = catalogData.value.meta.last_page;
  }
});

watch(pending, (value) => {
  if (!value) {
    if (infiniteScroll.value) {
      dataCatalog.value = [
        ...(dataCatalog.value || []),
        ...(catalogData.value?.data || []),
      ];
      infiniteScroll.value = false;
    } else {
      dataCatalog.value = catalogData.value?.data;
    }
  }
});

watchEffect(() => {
  if (isMounted.value && dataCatalog.value?.length) {
    viewItemListEvent(dataCatalog.value, {
      item_list_name: props.pageData?.currentTranslation?.title || t("catalog"),
    });
    dynamicParamsEvent(`View Category`, {
      ecomm_prodid: dataCatalog.value.map((item) => item.default_sku),
      ecomm_pagetype: "category",
      ecomm_totalvalue: "",
      ecomm_category: props.pageData?.currentTranslation?.title || t("catalog"),
    });
  }
});

// scroll top on search value change
watch(
  [() => filter.value.search, () => filter.value.page],
  ([searchVal, pageVal]) => {
    if (searchVal && isMounted.value && pageVal === 1) {
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    }
  },
);

function changePage() {
  infiniteScroll.value = true;
  if (root.value) {
    root.value.scrollIntoView({ block: "start", behavior: "smooth" });
  }
  dataCatalog.value = [];
}

function onChangeSort(e) {
  sortBy.value = e;
}

const {
  observeLoadMoreTopObserver,
  unObserveLoadMoreTopObserver,
  targetElTop,
} = useTopObserver();
onMounted(() => {
  setTimeout(() => {
    useInfiniteScroll(
      targetEl,
      () => {
        if (
          catalogData.value?.meta?.last_page &&
          catalogData.value.meta.last_page > page.value &&
          !pending.value &&
          !infiniteScroll.value
        ) {
          infiniteScroll.value = true;
          page.value++;
        }
      },
      { distance: 1000, interval: 300 },
    );

    observeLoadMoreTopObserver();
  }, 0);
});

onBeforeUnmount(() => {
  unObserveLoadMoreTopObserver();
});

setJSONLdSchema();

function setJSONLdSchema() {
  const name = props.pageData?.currentTranslation?.title || "";
  const image =
    getImageByPosition(props.pageData?.medias || [], "open-graph")?.file?.url ||
    getImageByPosition(props.pageData?.medias || [], "page")?.file?.url ||
    "";
  const numberOfItems = computed(() => catalogData.value?.meta?.total || 0);

  setPLPPageLdSchema({
    name,
    image,
    products: dataCatalog as Ref<CatalogData[]>,
    numberOfItems,
  });
}

function useTopObserver() {
  const loadMoreTopObserver = ref<IntersectionObserver | null>(null);
  const targetElTop = ref(null);

  async function loadMoreTopCallBack(entries) {
    if (
      catalogData.value?.meta?.last_page &&
      !pending.value &&
      !infiniteScroll.value &&
      dataCatalog.value?.length &&
      !prevDataLoading.value &&
      entries?.[0]?.isIntersecting
    ) {
      prevPage.value =
        filter.value.page -
        Math.ceil(dataCatalog.value.length / filter.value.count);

      if (!prevPage.value) {
        return;
      }
      infiniteScroll.value = true;
      prevDataLoading.value = true;
      await execute();

      const scrollHeight = window.document.documentElement.scrollHeight;
      const scrollTop = window.document.documentElement.scrollTop;

      dataCatalog.value = [
        ...(prevCatalogData.value?.data || []),
        ...(dataCatalog.value || []),
      ];
      await nextTick();

      const newScrollHeight = window.document.documentElement.scrollHeight;
      window.document.documentElement.scrollTop =
        newScrollHeight - scrollHeight + scrollTop;

      setTimeout(() => {
        prevDataLoading.value = false;
        infiniteScroll.value = false;
      }, 50);
    }
  }

  function observeLoadMoreTopObserver() {
    loadMoreTopObserver.value = new IntersectionObserver(loadMoreTopCallBack, {
      rootMargin: "400px 0px 0px 0px",
    });
    if (loadMoreTopObserver.value && targetElTop.value) {
      loadMoreTopObserver.value.observe(targetElTop.value);
    }
  }
  function unObserveLoadMoreTopObserver() {
    if (loadMoreTopObserver.value) {
      loadMoreTopObserver.value.disconnect();
    }
  }
  return {
    observeLoadMoreTopObserver,
    unObserveLoadMoreTopObserver,
    targetElTop,
  };
}

// Parse and convert Static filter url to filterValue and filterGroup
// Url format from admin panel "{filterGroup}|{filterValue}", default filterGroup is materials, even if it comes from the admin panel without a separator "|"
function setStaticFilterValues(item) {
  const splitedUrl = item.url?.replace(/[/?&\s]/g, "").split("|") || "";
  const filterGroup =
    splitedUrl.length > 1 && splitedUrl[0] && splitedUrl[1]
      ? splitedUrl[0]
      : null;
  item.filterValue =
    filterGroup && splitedUrl[1] ? splitedUrl[1] : splitedUrl[0];
  item.filterGroup = filterGroup || filterQueryKey.materials;
  return item;
}

function applyStaticFilter(item) {
  const queryFilterGroup = route.query[item.filterGroup];
  let queryGroupFilters = queryFilterGroup
    ? Array.isArray(queryFilterGroup)
      ? JSON.parse(JSON.stringify(queryFilterGroup))
      : [queryFilterGroup]
    : [];
  if (queryGroupFilters.includes(item.filterValue)) {
    queryGroupFilters = queryGroupFilters.filter(
      (filter) => filter !== item.filterValue,
    );
  } else {
    queryGroupFilters.push(item.filterValue);
  }
  router.replace({
    query: { ...route.query, [item.filterGroup]: queryGroupFilters },
  });
}

function checkActiveStaticFilter(item) {
  return (
    route.query[item.filterGroup] === item.filterValue ||
    route.query[item.filterGroup]?.includes(item.filterValue)
  );
}
</script>
